import type { LucideIcon } from 'lucide-react';
import {
  Apple,
  ArrowLeft,
  ArrowRight,
  Blocks,
  Bookmark,
  BookOpen,
  Box,
  Brain,
  Cake,
  Calculator,
  Car,
  CaseSensitive,
  Cat,
  Check,
  Cherry,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Cloud,
  Copy,
  Delete,
  Dog,
  Eraser,
  Eye,
  EyeOff,
  Film,
  Flower2,
  Grid2x2,
  HandGrab,
  Hash,
  Heart,
  House,
  ImageOff,
  Keyboard,
  LayoutGrid,
  Lightbulb,
  MessageCircle,
  MessageCircleQuestion,
  MessageSquare,
  MessageSquareText,
  Mic,
  Music,
  Paintbrush,
  Palette,
  PartyPopper,
  Pause,
  PawPrint,
  Pencil,
  Phone,
  Pizza,
  Plane,
  Play,
  Printer,
  Puzzle,
  RefreshCw,
  RotateCcw,
  Scissors,
  ScrollText,
  Search,
  Settings,
  Shapes,
  Shirt,
  Smile,
  Square,
  SquarePen,
  SquareStack,
  Star,
  Sun,
  ThumbsUp,
  TrainTrack,
  Sparkles,
  Dices,
  Trash2,
  Upload,
  User,
  Users,
  Volume2,
  WifiOff,
  X,
  Zap,
} from 'lucide-react';
import { ICON_SIZE, ICON_STROKE } from '../styles/tokens';

/**
 * Íconos semánticos de la app, sobre lucide-react.
 *
 * Acá no se dibuja nada: cada nombre es un concepto de Clarisa Hub ("memo",
 * "sílabas", "tarjetas") apuntando al glifo de lucide que lo representa. Es
 * el único lugar donde se decide qué dibujo significa qué, así que cambiar el
 * ícono de Memotest en toda la app es cambiar una línea.
 *
 * Los nombres también viven como DATOS: los de las categorías (`paw`, `apple`,
 * `heart`, `zap`, `box`, `user`, `star`…) se eligen en AdminConfig y quedan
 * guardados en D1. No renombres ni borres uno sin migrar esos valores.
 *
 * Antes esto era un registro de 46 íconos dibujados a mano en paths SVG, con
 * algunos problemas que se veían: Memotest y Memo Fácil usaban la misma grilla
 * que Tarjetas, el libro de Cuentos estaba cerrado y el de Música era un guiño
 * al logo de Spotify que para una nena de 3 años no significa nada.
 */
const ICONS = {
  // Tiles del home y secciones
  all: LayoutGrid,
  letter: CaseSensitive,
  syllable: Blocks,
  hash: Hash,
  palette: Palette,
  paint: Paintbrush, // Pintar: el pincel, distinto de la paleta de Colores
  shapes: Shapes,
  book: BookOpen,
  historia: ScrollText, // Historias JW: su propio glifo, distinto del libro de Cuentos
  comic: MessageSquareText,
  memo: Brain,
  mente: MessageCircleQuestion, // ¿Qué piensa?: antes compartía Brain con Memotest
  'memo-easy': Copy,
  drag: HandGrab,
  pencil: Pencil,
  mic: Mic,
  keyboard: Keyboard,
  search: Search,
  calculator: Calculator,
  count: Cherry, // Contar: dos cerezas, cosas para contar
  phone: Phone,
  music: Music,
  cards: SquareStack,
  sentence: Puzzle,
  rompecabezas: Grid2x2, // una foto cortada en piezas; el Puzzle ya es de Oraciones
  train: TrainTrack,
  sun: Sun,
  pop: Sparkles,
  machine: Dices,
  shirt: Shirt,

  // Categorías (elegibles en AdminConfig, persistidas en D1)
  star: Star,
  paw: PawPrint,
  apple: Apple,
  heart: Heart,
  user: User,
  zap: Zap,
  box: Box,
  home: House,
  smile: Smile,
  brain: Brain,

  // Acciones y navegación
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  play: Play,
  pause: Pause,
  stop: Square,
  check: Check,
  x: X,
  close: X,
  edit: SquarePen,
  trash: Trash2,
  refresh: RefreshCw, // actualizar / mezclar (no es "otra vez")
  replay: RotateCcw, // "Otra vez" y volver a empezar, en todos los juegos
  settings: Settings,
  volume: Volume2,
  backspace: Delete,
  eraser: Eraser,
  lightbulb: Lightbulb,
  printer: Printer,
  'wifi-off': WifiOff,
  pensamiento: Cloud,
  charla: MessageCircle,
  familia: Users,
  eye: Eye,
  'eye-off': EyeOff,
  help: CircleHelp,
  film: Film,
  scissors: Scissors,
  upload: Upload,
  party: PartyPopper,
  'thumbs-up': ThumbsUp,
  comment: MessageSquare,
  bookmark: Bookmark,
  'image-off': ImageOff,

  // Dibujos sueltos (objetos para contar en las tarjetas de Números)
  car: Car,
  flower: Flower2,
  cat: Cat,
  dog: Dog,
  plane: Plane,
  pizza: Pizza,
  cake: Cake,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

type Props = {
  name: IconName;
  /** Un valor de `ICON_SIZE` (tokens.ts), o un porcentaje (`"100%"`) si el ícono
   *  escala con su caja. Por defecto `ICON_SIZE.lg`. */
  size?: number | string;
  /** Grosor del trazo. Por defecto el del sistema (`ICON_STROKE`); pasalo
   *  sólo si un contexto necesita otro de verdad. */
  stroke?: number;
  /** Color del trazo (`currentColor` si se omite). */
  color?: string;
  /** Relleno del glifo (ninguno si se omite): para los dibujos de colores del
   *  contador de Números o los festejos. */
  fill?: string;
  className?: string;
};

export function Icon({ name, size = ICON_SIZE.lg, stroke = ICON_STROKE, color, fill, className }: Props) {
  const Glyph = ICONS[name];
  // `fill` y `color` se pasan sólo si vienen: un `fill={undefined}` pisaría el
  // `fill="none"` de lucide y todos los íconos saldrían rellenos de negro.
  const paint = { ...(fill !== undefined && { fill }), ...(color !== undefined && { color }) };
  return <Glyph size={size} strokeWidth={stroke} className={className} aria-hidden="true" {...paint} />;
}
